import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Info, Search, CheckCircle, Building } from "lucide-react";
import { TrainingInput } from "./types";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { fetchFilteredProviders, setProviderFilter } from "@/features/provider/providerSlice";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Provider } from "@/features/provider/types";
import { tunisianGovernorates, governorateCities } from "@/data/tunisian-governorates";
import { useToast } from "@/components/ui/use-toast";
import ProviderLogo from "@/components/common/ProviderLogo";

interface ProviderStepProps {
  newTraining: Partial<TrainingInput>;
  setNewTraining: (training: Partial<TrainingInput>) => void;
}

const ProviderStep: React.FC<ProviderStepProps> = ({ newTraining, setNewTraining }) => {
  const dispatch = useDispatch();
  const { providers, loading, error } = useSelector((state: RootState) => state.provider);
  const { toast } = useToast();

  // Local state for search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedCountry, setSelectedCountry] = useState("Tunisia");
  const [selectedState, setSelectedState] = useState("all");
  const [selectedCity, setSelectedCity] = useState("all");

  // Fetch providers on mount
  useEffect(() => {
    try {
      const initialFilter = {
        search: null,
        type: null,
        ownership: null,
        country: "Tunisia",
        city: null,
        state: null,
        category: null,
        page: 1,
        limit: 50,
      };
      dispatch(setProviderFilter(initialFilter));
      dispatch(fetchFilteredProviders(initialFilter) as any);
    } catch (err) {
      console.error("Error fetching providers:", err);
      toast({
        title: "Error",
        description: "Failed to load providers. Please try again.",
        variant: "destructive",
      });
    }
  }, [dispatch]);

  // Handle state change
  const handleStateChange = (value: string) => {
    setSelectedState(value);
    setSelectedCity("all");
  };

  // Apply filters
  const applyFilters = () => {
    try {
      const newFilter = {
        search: searchTerm || null,
        type: selectedType === "all" ? null : selectedType,
        country: selectedCountry === "all" ? null : selectedCountry,
        state: selectedState === "all" ? null : selectedState,
        city: selectedCity === "all" ? null : selectedCity,
        page: 1,
        limit: 50,
      };
      dispatch(setProviderFilter(newFilter));
      dispatch(fetchFilteredProviders(newFilter) as any);
    } catch (err) {
      console.error("Error applying filters:", err);
      toast({
        title: "Search Error",
        description: "Failed to search providers. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Select provider
  const selectProvider = (provider: Provider) => {
    try {
      if (!provider || !provider.id) throw new Error("Invalid provider selected");
      const providerId = typeof provider.id === "string" ? parseInt(provider.id, 10) : provider.id;
      if (isNaN(providerId)) throw new Error("Invalid provider ID format");
      setNewTraining({
        ...newTraining,
        provider: provider.name,
        providerId: providerId,
      });
    } catch (err) {
      console.error("Error selecting provider:", err);
      toast({
        title: "Selection Error",
        description: "Failed to select provider. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Find selected provider
  const selectedProvider =
    providers && Array.isArray(providers) && providers.length > 0
      ? providers.find((p) => p && p.id === newTraining.providerId)
      : undefined;

  // Helper functions
  const formatLogoUrl = (url: string | undefined) => {
    if (!url) return "";
    return url.startsWith("http") ? url : url;
  };

  const viewProviderDetails = (providerId: string, e: React.MouseEvent) => {
    e.preventDefault(); // Prevent form submission
    e.stopPropagation();
    window.open(`/admin/providers/${providerId}`, "_blank");
  };

  const formatProviderType = (type: string | undefined) => {
    if (!type) return "";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if ((e.target as HTMLElement).id === "provider-search") applyFilters();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="text-blue-500 h-5 w-5 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-700">Course Provider Information</h3>
            <p className="text-sm text-blue-600">
              Search and select an existing provider or enter a new provider name.
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    id="provider-search"
                    type="text"
                    placeholder="Search providers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
              <Button type="button" onClick={applyFilters}>
                Search
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger>
                  <SelectValue placeholder="Provider Type" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="university">University</SelectItem>
                  <SelectItem value="college">College</SelectItem>
                  <SelectItem value="institute">Institute</SelectItem>
                  <SelectItem value="academy">Academy</SelectItem>
                  <SelectItem value="school">School</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger>
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
              <SelectContent className="bg-white z-50">
                  <SelectItem value="Tunisia">Tunisia</SelectItem>
                  <SelectItem value="all">All Countries</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select value={selectedState} onValueChange={handleStateChange}>
                <SelectTrigger>
                  <SelectValue placeholder="State" />
                </SelectTrigger>
              <SelectContent className="bg-white z-50">
                  <SelectItem value="all">All States</SelectItem>
                  {tunisianGovernorates.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={selectedCity}
                onValueChange={setSelectedCity}
                disabled={selectedState === "all"}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={selectedState !== "all" ? "Select City" : "Select State First"}
                  />
                </SelectTrigger>
              <SelectContent className="bg-white z-50">
                  <SelectItem value="all">All Cities</SelectItem>
                  {selectedState !== "all" &&
                    governorateCities[selectedState]?.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provider Results */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-4">Select a Provider</h3>

          {/* **Improvement 1: Persistent Selected Provider Display** */}
          {newTraining.providerId ? (
            selectedProvider ? (
              <div className="mb-4 flex items-center gap-4 p-4 border rounded-md bg-muted/30">
                <ProviderLogo
                  filename={formatLogoUrl(selectedProvider.logoUrl)}
                  alt={`${selectedProvider.name} logo`}
                  className="w-16 h-16 rounded-md"
                />
                <div className="flex-1">
                  <h3 className="font-medium">{selectedProvider.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {formatProviderType(selectedProvider.type)} •{" "}
                    {selectedProvider.city && selectedProvider.country
                      ? `${selectedProvider.city}, ${selectedProvider.country}`
                      : selectedProvider.city || selectedProvider.country}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => viewProviderDetails(selectedProvider.id.toString(), e)}
                >
                  See Details
                </Button>
              </div>
            ) : (
              <div className="mb-4 p-4 border rounded-md bg-muted/30">
                <div className="flex items-center gap-4">
                  <Building className="h-12 w-12 text-muted-foreground" />
                  <div>
                    <h3 className="font-medium">{newTraining.provider}</h3>
                    <p className="text-sm text-muted-foreground">
                      Selected provider not in current filter
                    </p>
                  </div>
                </div>
              </div>
            )
          ) : null}

          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto mb-2"></div>
              <p>Loading providers...</p>
            </div>
          ) : error ? (
            <div className="text-center py-4 text-red-500">
              Error loading providers. Please try again.
            </div>
          ) : !providers || !Array.isArray(providers) || providers.length === 0 ? (
            <div className="text-center py-8 flex flex-col items-center">
              <Building className="h-12 w-12 text-muted-foreground mb-2" />
              <p>No providers found. Try different search criteria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 max-h-[400px] overflow-y-auto pr-1">
              {providers.map((provider) => (
                <div
                  key={provider.id}
                  className={`p-3 border rounded-md cursor-pointer hover:bg-muted transition-colors ${
                    newTraining.providerId === provider.id ? "bg-primary/10 border-primary" : ""
                  }`}
                  onClick={() => selectProvider(provider)}
                >
                  <div className="flex items-center gap-4">
                    <ProviderLogo
                      filename={formatLogoUrl(provider.logoUrl)}
                      alt={`${provider.name} logo`}
                      className="w-12 h-12 rounded-md"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{provider.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatProviderType(provider.type)} •{" "}
                        {provider.city && provider.state
                          ? `${provider.city}, ${provider.state}`
                          : provider.city || provider.state || provider.country}
                      </div>
                    </div>
                    {/* **Improvement 2: Enhanced List Indication** */}
                    {newTraining.providerId === provider.id && (
                      <span className="text-primary font-medium flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" /> Selected
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => viewProviderDetails(provider.id.toString(), e)}
                    >
                      See Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Provider Entry */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="provider" className="text-base">
                Provider Name {newTraining.providerId ? "(Selected)" : "(Manual Entry)"}
              </Label>
              {/* **Improvement 3: Input Field Clarity** */}
              <div className="relative">
                <Input
                  id="provider"
                  value={newTraining.provider || ""}
                  onChange={(e) => {
                    setNewTraining({
                      ...newTraining,
                      provider: e.target.value,
                      providerId: undefined,
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  placeholder="e.g. Tech Academy, Microsoft, or Individual Instructor"
                  className="h-10"
                  required
                />
                {newTraining.providerId && (
                  <span className="absolute right-2 top-2.5 text-green-500">
                    <CheckCircle className="h-5 w-5" />
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {newTraining.providerId
                  ? "Provider selected from list. Edit name to enter manually."
                  : "Enter provider name manually or select from list above."}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProviderStep;